import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Award, Target, Heart, MessageCircle } from 'lucide-react';
import FeedbackModal from './FeedbackModal';
import { useAuth } from '../contexts/AuthContext';

interface LeaderInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  leader: {
    name: string;
    title: string;
    avatar?: string;
    vision: string;
    attentes: string;
    pagePRIsME?: string;
    email?: string;
    departmentKey?: string;
  };
  feedbackContextLevel?: 'DEPARTMENT' | 'CARD';
  feedbackCardId?: number | null;
  feedbackContextLabel?: string;
}

export default function LeaderInfoModal({
  isOpen,
  onClose,
  leader,
  feedbackContextLevel = 'DEPARTMENT',
  feedbackCardId = null,
  feedbackContextLabel,
}: LeaderInfoModalProps) {
  const { user } = useAuth();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const currentEmail = user?.email?.toLowerCase() || '';
  const leaderEmail = leader.email ? leader.email.toLowerCase() : '';
  const isDany = currentEmail === 'dany.bourassa@bzinc.ca';
  const canOpenFeedback = isDany || (!!leaderEmail && currentEmail === leaderEmail);

  const departmentKey = leader.departmentKey || 'direction-ti';
  const contextLevel = feedbackContextLevel;
  const contextLabel =
    feedbackContextLabel ||
    (contextLevel === 'DEPARTMENT'
      ? `Service & Direction TI – ${leader.name}`
      : `Carte – ${leader.name}`);
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl max-w-2xl w-full my-8 border border-indigo-500/30 shadow-2xl shadow-indigo-500/20"
          >
            {/* Header */}
            <div className="relative p-6 border-b border-indigo-500/30">
              <div className="flex items-center gap-4">
                {leader.avatar && (
                  <div className="w-20 h-20 rounded-full ring-4 ring-indigo-500/50 overflow-hidden">
                    <img
                      src={leader.avatar}
                      alt={leader.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white">{leader.name}</h3>
                  <p className="text-indigo-300 text-lg">{leader.title}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {leader.pagePRIsME && (
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl p-4 border border-indigo-500/30">
                  <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-400" />
                    Page PRIsME
                  </h4>
                  <p className="text-gray-300">{leader.pagePRIsME}</p>
                </div>
              )}

              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/30">
                <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-purple-400" />
                  Vision
                </h4>
                <p className="text-gray-300 leading-relaxed">{leader.vision}</p>
              </div>

              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-4 border border-blue-500/30">
                <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-400" />
                  Attentes
                </h4>
                <p className="text-gray-300 leading-relaxed">{leader.attentes}</p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-indigo-500/30 bg-slate-900/50">
              <div className="flex items-center justify-between">
                {canOpenFeedback ? (
                  <button
                    onClick={() => setIsFeedbackOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500/90 text-white text-sm font-semibold hover:bg-indigo-600 transition-all shadow-md"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Commentaires (Amélioration continue)
                  </button>
                ) : (
                  <span className="text-[11px] text-slate-400">
                    Les commentaires sont gérés par le gestionnaire responsable et Dany.
                  </span>
                )}
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>

          {isFeedbackOpen && (
            <FeedbackModal
              isOpen={isFeedbackOpen}
              onClose={() => setIsFeedbackOpen(false)}
              departmentKey={departmentKey}
              contextLevel={contextLevel}
              cardId={contextLevel === 'CARD' ? feedbackCardId ?? null : null}
              contextLabel={contextLabel}
              leaderEmail={leader.email}
            />
          )}
        </div>
      )}
    </AnimatePresence>
  );
}
